import { CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import * as React from "react";

import {
  formsApi,
  type PreviewResponse,
} from "@/api/forms";
import { Button } from "@/components/ui/button";
import { DynamicForm } from "@/components/widgets/DynamicForm";
import { useFormBuilderStore } from "@/stores/formBuilder";
import { useUIStore } from "@/stores/ui";

/**
 * LivePreview — render form như user thấy + server-side preview submit.
 *
 * KHÔNG dùng iframe sandbox (đơn giản hơn, dùng DynamicForm widget có sẵn).
 * Khi user click submit → gọi /preview để dry-run validation.
 */
export function LivePreview(): JSX.Element {
  const schema = useFormBuilderStore((s) => s.schema);
  const formCode = useFormBuilderStore((s) => s.formCode);
  const versionInfo = useFormBuilderStore((s) => s.versionInfo);
  const isDirty = useFormBuilderStore((s) => s.isDirty);
  const pushToast = useUIStore((s) => s.pushToast);
  const [preview, setPreview] = React.useState<PreviewResponse | null>(null);
  const [busy, setBusy] = React.useState(false);

  if (!schema) return <div className="p-4 text-sm">Loading...</div>;

  const handleSubmit = async (data: Record<string, unknown>): Promise<void> => {
    if (!formCode) {
      pushToast({
        variant: "warning",
        title: "Cần lưu draft trước",
        description: "Preview server-side cần form được persist (ít nhất draft v1).",
      });
      return;
    }
    if (isDirty) {
      pushToast({
        variant: "warning",
        title: "Có thay đổi chưa lưu",
        description: "Save Draft để preview chính xác. Đang preview với version đã save.",
      });
    }
    setBusy(true);
    try {
      const res = await formsApi.preview(formCode, data, versionInfo?.version);
      setPreview(res);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Preview thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  // Convert schema fields → DynamicForm payload format (simple subset)
  const payload = {
    title: schema.title || "Preview",
    description: schema.description ?? undefined,
    submit_label: "Test submit",
    cancel_label: "Reset",
    fields: schema.fields.map((f) => ({
      name: f.name,
      type: f.type,
      label: f.label,
      required: f.required ?? false,
      placeholder: f.placeholder ?? undefined,
      help_text: f.help_text ?? undefined,
      min: f.min ?? undefined,
      max: f.max ?? undefined,
      min_length: f.min_length ?? undefined,
      max_length: f.max_length ?? undefined,
      options: f.options
        ? f.options.map((o) => ({ value: o.value, label: o.label }))
        : undefined,
      endpoint:
        f.binding && formCode
          ? `/api/v1/forms/${formCode}/${f.name}/search`
          : undefined,
      label_field: f.binding?.label_field ?? "name",
      value_field: "id",
      min_chars: f.min_chars ?? 2,
    })),
  };

  return (
    <div className="p-4 space-y-4 bg-muted/30 min-h-full">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Live preview</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setPreview(null)}
          disabled={busy}
        >
          <RefreshCw className="size-3.5 mr-1" /> Reset
        </Button>
      </div>

      <DynamicForm
        payload={payload}
        status="pending"
        onSubmit={(data) => void handleSubmit(data)}
        onCancel={() => setPreview(null)}
      />

      {preview ? (
        <div className="rounded-md border bg-background p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            {preview.is_valid ? (
              <>
                <CheckCircle2 className="size-4 text-green-600" /> Valid
              </>
            ) : (
              <>
                <XCircle className="size-4 text-destructive" /> Có lỗi
              </>
            )}
          </div>
          {Object.keys(preview.errors).length > 0 ? (
            <div className="text-xs space-y-0.5">
              <div className="font-medium text-destructive">Errors:</div>
              {Object.entries(preview.errors).map(([k, v]) => (
                <div key={k}>
                  <code className="bg-muted px-1 rounded">{k}</code>: {v}
                </div>
              ))}
            </div>
          ) : null}
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              Field states (visible/required)
            </summary>
            <pre className="bg-muted rounded p-2 mt-1 overflow-x-auto">
              {JSON.stringify(preview.field_states, null, 2)}
            </pre>
          </details>
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              Cleaned payload
            </summary>
            <pre className="bg-muted rounded p-2 mt-1 overflow-x-auto">
              {JSON.stringify(preview.cleaned, null, 2)}
            </pre>
          </details>
        </div>
      ) : null}
    </div>
  );
}
